import React from 'react';
import { cn } from '~/lib/utils';
import styles from './PillButton.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface PillButtonProps {
  /** The command, e.g. `Accept`, `Try again`. One line. */
  label: string;
  /** `primary` adds the light-10 fill; `ghost` is the same pill without it. Everything else is shared. */
  variant: 'primary' | 'ghost';
  onClick?: () => void;
  theme?: ChatTheme;
  className?: string;
}

/**
 * One way forward out of an agent state (Accept / Reject on *Complete*, Try again / Skip it on
 * *Failed*).
 *
 * 30px tall, 13/18 at weight 500, rim always present, label always `--cv15-text-subtle` — the
 * primary variant adds a fill and changes **nothing else**. That restraint is the point: the pair
 * is two available exits with one nearer to hand, not a call to action next to a way to back out.
 *
 * **Not `AnswerChip`.** That one is 34px with a 14/20 label and records a choice; this one is 30px
 * and fires a command. They are kept apart on purpose — see the note in `AnswerChip.tsx`.
 *
 * Paper draws the pill as a `div`; it is a real `<button>` here.
 */
export function PillButton({
  label,
  variant,
  onClick,
  theme: themeProp,
  className,
}: PillButtonProps) {
  const theme = useV15Theme(themeProp);
  return (
    <button
      type="button"
      className={cn(chatThemeClass(theme), styles.pill, styles[variant], className)}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export interface PillActionRowProps {
  /**
   * `stretched` (default) is *Complete*'s row: a full-width member of the assistant column.
   * `inline` is *Failed*'s: flex and gap only, hugging its pills at the column's start.
   */
  layout?: 'stretched' | 'inline';
  /** `PillButton`s — always a filled primary plus an outline ghost, in that order. */
  children: React.ReactNode;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The row a pair of pills sits in — an 8px gap, and a choice of how the row itself meets the
 * column.
 *
 * The gap is the same in both artboards; only the wrapper differs, which is why `layout` is the
 * one prop here and the 8px is not.
 */
export function PillActionRow({
  layout = 'stretched',
  children,
  theme: themeProp,
  className,
}: PillActionRowProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(chatThemeClass(theme), styles.row, styles[layout], className)}>
      {children}
    </div>
  );
}
